import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  getMyWebhookConfig,
  setMyWebhookConfig,
  fireMyWebhookTestEvent,
} from '../../services/liveStamping';

const VERIFY_PUBLIC_KEYS_URL = 'https://app.matrica.in/api/public-keys';
const ONLINE_RSA_VERIFY_DEMO_URL = 'https://emn178.github.io/online-tools/rsa/verify/';
const RSA_VERIFY_GUIDE_IMG = `${
  typeof process.env.PUBLIC_URL === 'string' ? process.env.PUBLIC_URL.replace(/\/$/, '') : ''
}/doc/webhook-rsa-verify-example.png`;

/** True when URL uses plain HTTP (not HTTPS). */
function isInsecureHttpUrl(url) {
  return url.trim().toLowerCase().startsWith('http://');
}

function InlineCode({ children }) {
  return (
    <span className="font-mono text-[13px] bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded">
      {children}
    </span>
  );
}

function SignatureVerificationPanel({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close verification guide"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-labelledby="signature-verify-panel-title"
        aria-modal="true"
        className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl animate-in slide-in-from-right duration-200"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id="signature-verify-panel-title" className="text-lg font-semibold text-gray-900">
              Verify webhook signatures
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Each POST includes headers and a canonical JSON body. Use them to authenticate that the event came from Matrica.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            <span aria-hidden className="text-xl leading-none">
              ×
            </span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm text-gray-700 leading-relaxed space-y-5">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Headers on delivery
            </h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <InlineCode>x-matrica-key-code</InlineCode>
                {' — '}which RSA key signed this request (matches an entry returned from the public keys endpoint).
              </li>
              <li>
                <InlineCode>x-matrica-timestamp</InlineCode>
                {' — '}Unix epoch seconds (<InlineCode>str</InlineCode>), same value used inside the signed string.
              </li>
              <li>
                <InlineCode>x-matrica-signature</InlineCode>
                {' — '}Base64-encoded signature over the preimage described below.
              </li>
            </ul>
            <p className="mt-3 text-xs text-gray-500">
              The POST body is minified canonical JSON (<InlineCode>sort_keys=true</InlineCode>, no extra spaces).
              Preserve the raw body bytes unchanged when verifying.
            </p>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              1. Load the matching public key
            </h3>
            <p>
              Fetch <InlineCode>{VERIFY_PUBLIC_KEYS_URL}</InlineCode> (JSON). Locate the array entry{' '}
              <InlineCode>keys[]</InlineCode> where <InlineCode>code</InlineCode> equals your request’s{' '}
              <InlineCode>x-matrica-key-code</InlineCode>; use its <InlineCode>publicKey</InlineCode>{' '}
              PEM field. Prefer a key with <InlineCode>isDeprecated: false</InlineCode>. Cache keys and refresh periodically.
            </p>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              2. Build what was signed
            </h3>
            <p className="mb-2">
              Let <InlineCode>t</InlineCode> be the value of <InlineCode>x-matrica-timestamp</InlineCode>{' '}
              <em>exactly</em> as received (digits only, no fractional part). Let <InlineCode>b</InlineCode>{' '}
              be the raw POST body (<strong className="text-gray-900">verbatim</strong> bytes from the HTTP message).
            </p>
            <p className="mb-2">The verifier reconstructs:</p>
            <pre className="rounded-lg bg-gray-900 text-gray-100 px-3 py-2.5 text-xs overflow-x-auto font-mono">
              {['signed_preview = `${t}.${body_utf8_decoded_as_string}`', 'signed_bytes   = UTF-8(signed_preview)'].join('\n')}
            </pre>
            <p className="mt-2 text-xs text-gray-500">
              Equivalent and often easier: concatenate the ASCII bytes <InlineCode>{'{t}.'}</InlineCode> with the{' '}
              <em>exact</em> POST body bytes (no decoding), then verify over that concatenation—the result matches the
              signer when the body is UTF-8.
            </p>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              3. Cryptography
            </h3>
            <p className="mb-2">
              Decode <InlineCode>x-matrica-signature</InlineCode> from Base64 into raw bytes.
            </p>
            <p className="mb-2">
              Verify an <strong className="text-gray-900">RSA PKCS #1 v1.5 signature (RSASSA-PKCS1-v1_5)</strong>{' '}
              using the <strong className="text-gray-900">SHA-256</strong>{' '}
              digest over <InlineCode>signed_bytes</InlineCode>.
            </p>
            <p className="text-xs text-gray-500">
              If verification fails after key rotation: retry with PEMs keyed by{' '}
              <InlineCode>code</InlineCode>; old keys remain listed until fully deprecated on your integration window.
            </p>
          </section>

          <section className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 px-4 pt-4 mb-2">
              Quick manual check (third-party demo)
            </h3>
            <p className="px-4 text-xs text-gray-600 mb-3">
              The{' '}
              <a
                href={ONLINE_RSA_VERIFY_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 underline"
              >
                RSA Verify Signature
              </a>{' '}
              tool illustrates the same flow: UTF-8 input combining <InlineCode>x-matrica-timestamp</InlineCode>, a dot,
              and the raw POST body; public key PEM from{' '}
              <InlineCode>{VERIFY_PUBLIC_KEYS_URL}</InlineCode> (match <InlineCode>x-matrica-key-code</InlineCode>);{' '}
              Base64 signature from <InlineCode>x-matrica-signature</InlineCode>; algorithm{' '}
              <strong>RSA PKCS #1 v1.5</strong>
              {' with '}
              <strong>SHA-256</strong> (shown as Signature Algorithm SHA256).
            </p>
            <figure className="m-0 border-t border-gray-200 bg-white">
              <img
                src={RSA_VERIFY_GUIDE_IMG}
                alt="Annotated screenshot of RSA Verify Signature demo: Input shows timestamp dot JSON body labeled as webhook headers fields; PEM public key and Base64 signature from Matrica headers; output Valid."
                className="block w-full h-auto max-h-[min(72vh,900px)] object-contain object-top bg-white"
                loading="lazy"
              />
              <figcaption className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-100 bg-gray-50">
                Annotated walkthrough tying Matrica headers and body fields to fields in an online verifier (sanity /
                demos only—a production integration should verify in your own codebase).
              </figcaption>
            </figure>
          </section>
        </div>
      </aside>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : null;
}

const WebhookSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firingTest, setFiringTest] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [allowUnsafeWebhook, setAllowUnsafeWebhook] = useState(false);
  const [error, setError] = useState('');
  const [signaturePanelOpen, setSignaturePanelOpen] = useState(false);

  const trimmedUrl = webhookUrl.trim();
  const showUnsafeWarning = trimmedUrl.length > 0 && isInsecureHttpUrl(trimmedUrl);
  const unsafeBlocked = showUnsafeWarning && !allowUnsafeWebhook;

  const loadWebhookConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMyWebhookConfig();
      setWebhookUrl(data?.webhookUrl || '');
    } catch (err) {
      setError(err.message || 'Failed to load webhook config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebhookConfig();
  }, []);

  const handleUrlChange = (e) => {
    const v = e.target.value;
    setWebhookUrl(v);
    if (!isInsecureHttpUrl(v)) {
      setAllowUnsafeWebhook(false);
    }
  };

  const handleSave = async () => {
    const trimmedUrl = webhookUrl.trim();
    if (!trimmedUrl) {
      setError('Webhook URL is required');
      return;
    }
    if (isInsecureHttpUrl(trimmedUrl) && !allowUnsafeWebhook) {
      setError('Confirm that you accept an insecure HTTP webhook URL.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await setMyWebhookConfig(trimmedUrl);
      toast.success('Webhook URL updated');
      await loadWebhookConfig();
    } catch (err) {
      setError(err.message || 'Failed to save webhook URL');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    if (isInsecureHttpUrl(trimmedUrl) && !allowUnsafeWebhook) {
      setError('Confirm that you accept an insecure HTTP webhook URL.');
      return;
    }
    setFiringTest(true);
    setError('');
    try {
      const data = await fireMyWebhookTestEvent();
      toast.success(`Test event queued (${data?.eventType || 'event'})`);
    } catch (err) {
      setError(err.message || 'Failed to publish test webhook event');
    } finally {
      setFiringTest(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-2xl">
      <SignatureVerificationPanel open={signaturePanelOpen} onClose={() => setSignaturePanelOpen(false)} />

      <div className="mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Webhook Settings</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure your delivery URL and send a random test event. Your session identifies the account.
        </p>
        <p className="mt-3 text-sm">
          <button
            type="button"
            onClick={() => setSignaturePanelOpen(true)}
            className="text-indigo-600 underline hover:text-indigo-800 decoration-indigo-300 underline-offset-2 font-medium cursor-pointer bg-transparent border-none p-0 text-left inline"
          >
            How to verify the webhook signature
          </button>
          <span className="text-gray-500">{` — RSA public-key verification of each delivery.`}</span>
        </p>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
        <input
          type="text"
          value={webhookUrl}
          onChange={handleUrlChange}
          placeholder="https://example.com/webhook"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      </div>

      {showUnsafeWarning && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={allowUnsafeWebhook}
              onChange={(e) => setAllowUnsafeWebhook(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400"
            />
            <span className="text-sm text-amber-900">
              <span className="font-medium">Allow unsafe webhook URL</span>
              {' — '}uses{' '}
              <span className="font-mono font-medium">http://</span>{' '}
              instead of encrypted{' '}
              <span className="font-mono font-medium">https://</span>. Only enable for trusted local or test setups.
            </span>
          </label>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || !trimmedUrl || unsafeBlocked}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save URL'}
        </button>
        <button
          onClick={handleTestWebhook}
          disabled={firingTest || !trimmedUrl || unsafeBlocked}
          className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {firingTest ? 'Sending Test...' : 'Test Webhook'}
        </button>
      </div>

      {error && (
        <div className="mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

export default WebhookSettings;
